import { createSlice } from '@reduxjs/toolkit';

/**
 * Initial state for student submission management
 */
const initialState = {
  // Current submission being worked on
  currentSubmission: null,
  currentAssessment: null,

  // Upload state
  uploadProgress: {}, // { fileId: percentage }
  isUploading: false,

  // Camera capture state
  isCameraOpen: false,
  capturedImages: [], // Temporary storage for captured images before upload

  // Submission history
  submissionHistory: [],
  historyPagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  historyFilters: {
    status: '', // 'DRAFT', 'SUBMITTED', 'LATE_SUBMITTED', 'EVALUATED'
  },

  // UI state
  loading: false,
  error: null,
  submissionSuccess: false,
  receipt: null,

  // Selected submission for viewing
  selectedSubmission: null,
};

const studentSubmissionSlice = createSlice({
  name: 'studentSubmission',
  initialState,
  reducers: {
    // ============ Loading & Error ============
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.isUploading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    // ============ Current Submission ============
    setCurrentSubmission: (state, action) => {
      const { submission, assessment } = action.payload;
      state.currentSubmission = submission;
      state.currentAssessment = assessment;
      state.loading = false;
      state.error = null;
    },

    clearCurrentSubmission: (state) => {
      state.currentSubmission = null;
      state.currentAssessment = null;
      state.uploadProgress = {};
      state.capturedImages = [];
      state.submissionSuccess = false;
      state.receipt = null;
    },

    // ============ File Upload ============
    setUploading: (state, action) => {
      state.isUploading = action.payload;
    },

    setUploadProgress: (state, action) => {
      const { fileId, progress } = action.payload;
      state.uploadProgress[fileId] = progress;
    },

    clearUploadProgress: (state, action) => {
      const { fileId } = action.payload;
      delete state.uploadProgress[fileId];
    },

    addFileToSubmission: (state, action) => {
      if (state.currentSubmission) {
        state.currentSubmission.files.push(action.payload);
        state.currentSubmission.totalPages = state.currentSubmission.files.length;
      }
      state.isUploading = false;
    },

    updateSubmissionFiles: (state, action) => {
      if (state.currentSubmission) {
        state.currentSubmission.files = action.payload;
        state.currentSubmission.totalPages = action.payload.length;
      }
    },

    removeFileFromSubmission: (state, action) => {
      const fileId = action.payload;
      if (state.currentSubmission) {
        state.currentSubmission.files = state.currentSubmission.files.filter(
          (f) => f.fileId !== fileId
        );
        // Reindex page numbers
        state.currentSubmission.files.forEach((file, idx) => {
          file.pageNumber = idx + 1;
        });
        state.currentSubmission.totalPages = state.currentSubmission.files.length;
      }
    },

    reorderFiles: (state, action) => {
      const newOrder = action.payload; // Array of fileIds in new order
      if (state.currentSubmission) {
        const fileMap = new Map(
          state.currentSubmission.files.map((f) => [f.fileId, f])
        );
        state.currentSubmission.files = newOrder.map((fileId, idx) => {
          const file = fileMap.get(fileId);
          if (file) {
            return { ...file, pageNumber: idx + 1 };
          }
          return null;
        }).filter(Boolean);
        state.currentSubmission.totalPages = state.currentSubmission.files.length;
      }
    },

    // ============ Camera Capture ============
    setCameraOpen: (state, action) => {
      state.isCameraOpen = action.payload;
    },

    addCapturedImage: (state, action) => {
      state.capturedImages.push(action.payload);
    },

    removeCapturedImage: (state, action) => {
      state.capturedImages = state.capturedImages.filter(
        (_, index) => index !== action.payload
      );
    },

    clearCapturedImages: (state) => {
      state.capturedImages = [];
    },

    // ============ Submission ============
    setSubmissionSuccess: (state, action) => {
      const { submission, receipt } = action.payload;
      state.currentSubmission = submission;
      state.submissionSuccess = true;
      state.receipt = receipt;
      state.loading = false;
    },

    clearSubmissionSuccess: (state) => {
      state.submissionSuccess = false;
      state.receipt = null;
    },

    // ============ Submission History ============
    setSubmissionHistory: (state, action) => {
      const { submissions, pagination } = action.payload;
      state.submissionHistory = submissions;
      state.historyPagination = pagination;
      state.loading = false;
    },

    appendSubmissionHistory: (state, action) => {
      const { submissions, pagination } = action.payload;
      state.submissionHistory = [...state.submissionHistory, ...submissions];
      state.historyPagination = pagination;
      state.loading = false;
    },

    setHistoryFilters: (state, action) => {
      state.historyFilters = { ...state.historyFilters, ...action.payload };
    },

    resetHistoryFilters: (state) => {
      state.historyFilters = initialState.historyFilters;
    },

    // ============ Selected Submission ============
    setSelectedSubmission: (state, action) => {
      const { submission, assessment } = action.payload;
      state.selectedSubmission = { submission, assessment };
      state.loading = false;
    },

    clearSelectedSubmission: (state) => {
      state.selectedSubmission = null;
    },

    // ============ Reset ============
    resetSubmissionState: () => initialState,
  },
});

// Export actions
export const {
  setLoading,
  setError,
  clearError,
  setCurrentSubmission,
  clearCurrentSubmission,
  setUploading,
  setUploadProgress,
  clearUploadProgress,
  addFileToSubmission,
  updateSubmissionFiles,
  removeFileFromSubmission,
  reorderFiles,
  setCameraOpen,
  addCapturedImage,
  removeCapturedImage,
  clearCapturedImages,
  setSubmissionSuccess,
  clearSubmissionSuccess,
  setSubmissionHistory,
  appendSubmissionHistory,
  setHistoryFilters,
  resetHistoryFilters,
  setSelectedSubmission,
  clearSelectedSubmission,
  resetSubmissionState,
} = studentSubmissionSlice.actions;

// ============ Selectors ============

// Current submission
export const selectCurrentSubmission = (state) =>
  state.studentSubmission.currentSubmission;
export const selectCurrentAssessment = (state) =>
  state.studentSubmission.currentAssessment;
export const selectSubmissionFiles = (state) =>
  state.studentSubmission.currentSubmission?.files || [];
export const selectTotalPages = (state) =>
  state.studentSubmission.currentSubmission?.totalPages || 0;

// Upload state
export const selectIsUploading = (state) => state.studentSubmission.isUploading;
export const selectUploadProgress = (state) =>
  state.studentSubmission.uploadProgress;

// Camera state
export const selectIsCameraOpen = (state) => state.studentSubmission.isCameraOpen;
export const selectCapturedImages = (state) =>
  state.studentSubmission.capturedImages;

// Submission result
export const selectSubmissionSuccess = (state) =>
  state.studentSubmission.submissionSuccess;
export const selectReceipt = (state) => state.studentSubmission.receipt;

// History
export const selectSubmissionHistory = (state) =>
  state.studentSubmission.submissionHistory;
export const selectHistoryPagination = (state) =>
  state.studentSubmission.historyPagination;
export const selectHistoryFilters = (state) =>
  state.studentSubmission.historyFilters;

// Selected submission
export const selectSelectedSubmission = (state) =>
  state.studentSubmission.selectedSubmission;

// UI state
export const selectSubmissionLoading = (state) => state.studentSubmission.loading;
export const selectSubmissionError = (state) => state.studentSubmission.error;

// Derived selectors
export const selectCanSubmit = (state) => {
  const submission = state.studentSubmission.currentSubmission;
  return (
    submission &&
    submission.status === 'DRAFT' &&
    submission.files &&
    submission.files.length > 0
  );
};

export const selectIsLateSubmission = (state) => {
  const assessment = state.studentSubmission.currentAssessment;
  if (!assessment?.dueDate) return false;
  return new Date() > new Date(assessment.dueDate);
};

export default studentSubmissionSlice.reducer;
